import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTrip, deleteExpense } from '../lib/db';
import { useAuth } from '../lib/auth';
import { track } from '../lib/analytics';
import { money, formatDay, wfmt } from '../lib/money';
import { coverersOf, paymentsFor, sharesFor } from '../lib/split';
import { CATEGORY_LABELS } from '../lib/exports';
import { ColorDot, Loading, Note, Page, Section, Sheet, useToast } from '../components/ui';
import { BackButton } from './Trip';
import AddExpense from './AddExpense';
import type { TripData } from '../types';

export default function ExpenseDetail() {
  const { groupId = '', expenseId = '' } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { data, loading } = useTrip(groupId);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading && !data) return <Loading />;

  const expense = data?.expenses.find((e) => e.id === expenseId);
  const back = () => nav(`/t/${groupId}`);

  if (!data || !expense) {
    return (
      <Page title="Expense" left={<BackButton onClick={back} />} noFab>
        <Note>That expense has been deleted.</Note>
        <button className="btn ghost" style={{ marginTop: 14 }} onClick={back}>
          Back to the trip
        </button>
      </Page>
    );
  }

  const { group, units } = data as TripData;
  const shares = sharesFor(units, expense);
  const payer = units.find((u) => u.id === expense.paidByUnitId);
  const payments = paymentsFor(expense);
  const coverers = coverersOf(units, expense);
  const basisLabel = expense.basis === 'perUnit' ? 'Split evenly per family'
    : expense.basis === 'custom' ? 'Custom amounts' : 'Split per person';

  async function remove() {
    setDeleting(true);
    setError(null);
    try {
      await deleteExpense(groupId, expenseId);
      track('expense_deleted');
      toast('Expense deleted');
      back();
    } catch (e) {
      setError((e as Error).message);
      setDeleting(false);
    }
  }

  const closeConfirm = () => { if (!deleting) { setConfirming(false); setError(null); } };

  return (
    <Page title="Expense" left={<BackButton onClick={back} />} noFab>
      <div className="det-amt">
        <div className="v amt">{money(expense.amountMinor, group.currency)}</div>
        <div className="m">{expense.desc}</div>
      </div>

      <div className="list" style={{ marginTop: 16 }}>
        <div className="kv"><span className="k">Paid by</span>
          <span className="v">
            {payer?.name ?? '—'}
            {coverers.length > 0 && ` · ${money(payments[expense.paidByUnitId], group.currency)}`}
          </span></div>
        {coverers.map((u) => (
          <div className="kv" key={u.id}><span className="k">Covered by</span>
            <span className="v">{u.name} · {money(payments[u.id], group.currency)}</span></div>
        ))}
        <div className="kv"><span className="k">Date</span>
          <span className="v">{formatDay(expense.date)}</span></div>
        <div className="kv"><span className="k">Category</span>
          <span className="v">{CATEGORY_LABELS[expense.category] ?? 'Other'}</span></div>
        <div className="kv"><span className="k">Method</span>
          <span className="v">{basisLabel}</span></div>
        {expense.note && (
          <div className="kv"><span className="k">Note</span>
            <span className="v">{expense.note}</span></div>
        )}
      </div>

      {expense.receiptUrl && (
        <>
          <Section label="Receipt" />
          <img className="rc-img" src={expense.receiptUrl} alt="Receipt" />
        </>
      )}

      <Section
        label="The split"
        action={<span className="muted" style={{ fontSize: 12 }}>
          {Object.keys(shares).length} families
        </span>}
      />
      <div className="list">
        {units.filter((u) => shares[u.id]).map((u) => {
          let detail = 'set manually';
          if (expense.basis === 'perPerson') {
            detail = u.people
              .filter((p) => expense.participantPersonIds.includes(p.id) && p.weight > 0)
              .map((p) => p.name + (p.weight !== 1 ? ` (${wfmt(p.weight)})` : ''))
              .join(', ');
          } else if (expense.basis === 'perUnit') {
            detail = '1 family share';
          }
          return (
            <div className="row" key={u.id}>
              <ColorDot color={u.color} />
              <span className="main">
                <span className="t">{u.name}</span>
                <span className="s">{detail}</span>
              </span>
              <span className="end">
                <span className="t amt">{money(shares[u.id], group.currency)}</span>
              </span>
            </div>
          );
        })}
      </div>

      <div className="xacts" style={{ marginTop: 18 }}>
        <button className="btn ghost" onClick={() => setEditing(true)}>Edit</button>
        <button className="btn danger" onClick={() => setConfirming(true)}>Delete</button>
      </div>

      {editing && user && (
        <AddExpense
          data={data}
          uid={user.uid}
          expense={expense}
          onClose={() => setEditing(false)}
          onSaved={(label) => { setEditing(false); toast(label); }}
        />
      )}

      {/* A delete moves every family's balance, so it takes a second, deliberate tap. */}
      {confirming && (
        <Sheet
          title="Delete this expense?"
          onClose={closeConfirm}
          footer={
            <div className="xacts">
              <button className="btn ghost" disabled={deleting} onClick={closeConfirm}>
                Keep it
              </button>
              <button className="btn danger" disabled={deleting} onClick={() => void remove()}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          }
        >
          <p style={{ fontSize: 14, lineHeight: 1.55, margin: '2px 2px 0' }}>
            <b>{expense.desc}</b> · {money(expense.amountMinor, group.currency)} comes off the
            trip, and every family's balance is recalculated without it.
          </p>
          {error && <div style={{ marginTop: 14 }}><Note>Couldn't delete that: {error}</Note></div>}
        </Sheet>
      )}
    </Page>
  );
}
